function isValidCssColor(value) {
  if (typeof value !== 'string') {
    return false;
  }

  const color = value.trim();
  if (!color) {
    return false;
  }

  return typeof window !== 'undefined' && window.CSS && window.CSS.supports('color', color);
}

export function applyPromoDesign(design = null) {
  if (typeof document === 'undefined' || !design) {
    return () => {};
  }

  const root = document.documentElement;
  const body = document.body;
  const colors = design.colors || {};

  const updates = [];

  if (isValidCssColor(colors.primary)) {
    updates.push(['--green-blue', colors.primary.trim()]);
  }

  if (isValidCssColor(colors.secondary)) {
    updates.push(['--bright-cyan-teal', colors.secondary.trim()]);
  }

  if (isValidCssColor(colors.background)) {
    updates.push(['--transparent-pale-aqua', colors.background.trim()]);
  }

  const previousValues = updates.map(([name]) => [name, root.style.getPropertyValue(name)]);
  updates.forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });

  const previousTheme = body.getAttribute('data-promo-theme');
  const previousThemeId = body.getAttribute('data-promo-theme-id');
  const previousImgBase = body.getAttribute('data-promo-img-base');

  if (design.theme) {
    body.setAttribute('data-promo-theme', String(design.theme));
  }

  if (design.theme_id) {
    body.setAttribute('data-promo-theme-id', String(design.theme_id));
  }

  if (design.imgurl) {
    body.setAttribute('data-promo-img-base', String(design.imgurl));
  }

  return () => {
    previousValues.forEach(([name, previous]) => {
      if (previous) {
        root.style.setProperty(name, previous);
      } else {
        root.style.removeProperty(name);
      }
    });

    if (previousTheme) {
      body.setAttribute('data-promo-theme', previousTheme);
    } else {
      body.removeAttribute('data-promo-theme');
    }

    if (previousThemeId) {
      body.setAttribute('data-promo-theme-id', previousThemeId);
    } else {
      body.removeAttribute('data-promo-theme-id');
    }

    if (previousImgBase) {
      body.setAttribute('data-promo-img-base', previousImgBase);
    } else {
      body.removeAttribute('data-promo-img-base');
    }
  };
}

export function applyPromoCustomCode(customCode = null) {
  if (typeof document === 'undefined' || !customCode) {
    return () => {};
  }

  const createdNodes = [];

  const appendStyle = (cssText, slot = 'head-css') => {
    extractStyleBlocks(cssText).forEach((block) => {
      if (!String(block || '').trim()) {
        return;
      }

      const styleTag = document.createElement('style');
      styleTag.setAttribute('data-promo-runtime', slot);
      styleTag.textContent = block;
      document.head.appendChild(styleTag);
      createdNodes.push(styleTag);
    });
  };

  const appendScript = (scriptText, slot) => {
    const target = slot === 'footer-js' ? document.body : document.head;

    extractScriptBlocks(scriptText).forEach((block, index) => {
      const scriptTag = document.createElement('script');
      scriptTag.type = 'text/javascript';
      scriptTag.setAttribute('data-promo-runtime', `${slot}-${index}`);

      if (block.src) {
        scriptTag.src = block.src;
        scriptTag.async = false;
        Object.entries(block.attributes || {}).forEach(([key, value]) => {
          if (key === 'src') {
            return;
          }

          if (value === '' || value === key) {
            scriptTag.setAttribute(key, '');
          } else {
            scriptTag.setAttribute(key, value);
          }
        });
        target.appendChild(scriptTag);
        createdNodes.push(scriptTag);
        return;
      }

      const inlineCode = String(block.code || '').trim();
      if (!inlineCode) {
        return;
      }

      if (!isValidInlineJavaScript(inlineCode)) {
        // Skip invalid JS so promo runtime never crashes.
        return;
      }

      scriptTag.text = inlineCode;
      target.appendChild(scriptTag);
      createdNodes.push(scriptTag);
    });
  };

  appendStyle(customCode.head_css);
  appendScript(customCode.head_js, 'head-js');
  appendScript(customCode.analytic_js, 'analytic-js');
  appendScript(customCode.conversion_js, 'conversion-js');
  appendScript(customCode.footer_js, 'footer-js');

  return () => {
    createdNodes.forEach((node) => node.remove());
  };
}

function extractStyleBlocks(value) {
  const source = stripHtmlComments(String(value || '')).trim();
  if (!source) {
    return [];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(source, 'text/html');
  const styleNodes = Array.from(doc.querySelectorAll('style'));

  if (styleNodes.length) {
    const extracted = styleNodes
      .map((node) => stripLegacyStyleWrappers(node.textContent || '').trim())
      .filter((item) => String(item || '').trim());

    const residual = stripLegacyStyleWrappers(
      source.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    ).trim();

    return residual ? [...extracted, residual] : extracted;
  }

  const inlineCss = stripLegacyStyleWrappers(source).trim();
  return inlineCss ? [inlineCss] : [];
}

function extractScriptBlocks(value) {
  const source = stripHtmlComments(String(value || '')).trim();
  if (!source) {
    return [];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(source, 'text/html');
  const scriptNodes = Array.from(doc.querySelectorAll('script'));

  if (!scriptNodes.length) {
    const inlineCode = normalizeInlineScriptCode(source);
    return inlineCode ? [{ code: inlineCode, src: null, attributes: {} }] : [];
  }

  const blocks = scriptNodes.map((node) => {
    const attrs = {};
    Array.from(node.attributes || []).forEach((attr) => {
      attrs[attr.name] = attr.value ?? '';
    });

    return {
      src: node.getAttribute('src'),
      code: normalizeInlineScriptCode(node.textContent || ''),
      attributes: attrs,
    };
  });

  const residualCode = normalizeInlineScriptCode(
    source.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
  );

  if (residualCode) {
    blocks.push({ code: residualCode, src: null, attributes: {} });
  }

  return blocks;
}

function isValidInlineJavaScript(code) {
  try {
    // Validate syntax only; execution happens via script tag.
    // eslint-disable-next-line no-new-func
    new Function(code);
    return true;
  } catch (error) {
    return false;
  }
}

function stripHtmlComments(value) {
  return String(value || '').replace(/<!--[\s\S]*?-->/g, '');
}

function stripLegacyScriptWrappers(value) {
  return String(value || '').replace(/<\/?script\b[^>]*>/gi, '');
}

function stripLegacyStyleWrappers(value) {
  return String(value || '').replace(/<\/?style\b[^>]*>/gi, '');
}

function normalizeInlineScriptCode(value) {
  return stripLegacyScriptWrappers(stripHtmlComments(value)).trim();
}
